import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Home from "./pages/Home";
import AuthCallback from "./pages/AuthCallback";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Account from "./pages/Account";
import Login from "./pages/Login";
import Admin from "./pages/Admin";
import Staff from "./pages/Staff";
import Kitchen from "./pages/Kitchen";
import PrintBusinessCard from "./pages/PrintBusinessCard";
import PrintMenu from "./pages/PrintMenu";
import PrintMerch from "./pages/PrintMerch";
import LogoPicker from "./pages/LogoPicker";
import BlogList, { BlogPost } from "./pages/Blog";
import SuburbPage from "./pages/SuburbPage";
import OrderTracker from "./pages/OrderTracker";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { TableSessionProvider } from "./context/TableSessionContext";
import CartDrawer from "./components/CartDrawer";
import AuthModal from "./components/AuthModal";
import FirstVisitPromo from "./components/FirstVisitPromo";
import StickyOrderBar from "./components/StickyOrderBar";
import TableRibbon from "./components/TableRibbon";
import CustomDomainRibbon from "./components/CustomDomainRibbon";
import TodaysSpecialBanner from "./components/TodaysSpecialBanner";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
import LiveActivityToast from "./components/LiveActivityToast";
import ConciergeChat from "./components/ConciergeChat";
import { Toaster } from "./components/ui/toaster";
import { useToast } from "./hooks/use-toast";
import { usePromoLanding } from "./hooks/usePromoLanding";

/**
 * Once the owner points mamaminassoulfeed.online at the deployment, they set
 * REACT_APP_CANONICAL_HOST=mamaminassoulfeed.online and rebuild. Visitors hitting
 * the raw emergent.host URL are then redirected to the branded domain, path
 * and query preserved. If the env var is empty (default), no redirect fires.
 */
function CanonicalHostRedirect() {
  useEffect(() => {
    const canonical = (process.env.REACT_APP_CANONICAL_HOST || "").trim().toLowerCase();
    if (!canonical) return;
    const here = window.location.hostname.toLowerCase();
    // Do not redirect off the canonical host itself, off localhost, or off a preview URL.
    if (here === canonical) return;
    if (here === "localhost" || here.endsWith(".preview.emergentagent.com")) return;
    // Only redirect from the platform-issued host to avoid stealing traffic from anything else.
    if (!here.endsWith(".emergent.host")) return;
    window.location.replace(`https://${canonical}${window.location.pathname}${window.location.search}${window.location.hash}`);
  }, []);
  return null;
}

// Overlays that make sense on the customer site but not on /kitchen (wall-TV),
// /admin (owner dashboard), /staff (till workflow) or /print (physical output).
// AuthModal is kept everywhere except /kitchen because /staff has a password
// fallback login that opens it.
function SiteOverlays() {
  const { pathname } = useLocation();
  const { toast } = useToast();
  // Detect ?promo=WELCOME10 / ?ref=welcome on any customer page load. Hook
  // stashes the code in sessionStorage so CartDrawer can auto-fill later.
  usePromoLanding(toast);

  if (pathname === "/kitchen") return null;
  if (pathname.startsWith("/print/")) return null;
  if (pathname === "/logo-picker") return null;
  if (pathname === "/login") return null;
  if (pathname.startsWith("/order/")) return null;  // OrderTracker owns its own frame
  const isBackOffice = pathname.startsWith("/staff") || pathname.startsWith("/admin");
  return (
    <>
      {!isBackOffice && <CustomDomainRibbon />}
      {!isBackOffice && <TableRibbon />}
      {!isBackOffice && <CartDrawer />}
      <AuthModal />
      {!isBackOffice && <FirstVisitPromo />}
      {!isBackOffice && <StickyOrderBar />}
      {!isBackOffice && <PwaInstallPrompt />}
      {!isBackOffice && <LiveActivityToast />}
      {!isBackOffice && <ConciergeChat />}
    </>
  );
}

// Thin "Today's Special" strip at the very top of every customer page.
// Component hides itself on /admin, /staff, /kitchen and /print (see component
// source), so it's safe to render globally here.
function TopBanner() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin") || pathname.startsWith("/staff") ||
      pathname.startsWith("/kitchen") || pathname.startsWith("/print") ||
      pathname === "/logo-picker" || pathname === "/login" ||
      pathname.startsWith("/order/")) return null;
  return <TodaysSpecialBanner variant="strip" />;
}

// Detect the OAuth session_id fragment synchronously during render — a
// useEffect would run after the first paint and race the normal routes.
function HashAuthGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

function App() {
  return (
    <div className="App">
      <CanonicalHostRedirect />
      <AuthProvider>
        <TableSessionProvider>
          <CartProvider>
            <BrowserRouter>
              <TopBanner />
              <HashAuthGate>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/account" element={<Account />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/staff" element={<Staff />} />
                <Route path="/kitchen" element={<Kitchen />} />
                <Route path="/print/card" element={<PrintBusinessCard />} />
                <Route path="/print/menu" element={<PrintMenu />} />
                <Route path="/print/merch" element={<PrintMerch />} />
                <Route path="/logo-picker" element={<LogoPicker />} />
                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/delivery/:slug" element={<SuburbPage />} />
                <Route path="/order/:sessionId" element={<OrderTracker />} />
              </Routes>
              </HashAuthGate>
              <SiteOverlays />
            </BrowserRouter>
          </CartProvider>
        </TableSessionProvider>
      </AuthProvider>
      <Toaster />
    </div>
  );
}

export default App;
